import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';

const LABELS: SocialPostPreviewCardMetadata['labels'] = {
  badge: 'Preview',
  postsNow: 'Goes out as soon as you approve it',
  scheduled: 'Scheduled for',
  textOnly: 'Text only',
  seeMore: 'See more',
  seeLess: 'See less',
};

const unsplash = (id: string, size = 'w=900&h=1350') => `https://images.unsplash.com/photo-${id}?${size}&q=80&auto=format&fit=crop`;
const image = (url: string): SocialPostPreviewMedia => ({ url, kind: 'image' });

const PIN_TALL = image(unsplash('1544367567-0f2fcb009e0b'));
const PIN_SQUARE = image(unsplash('1517836357463-d25dfeac3438', 'w=1000&h=1000'));
const PIN_WIDE = image(unsplash('1534438327276-14e5300c3a48', 'w=1200&h=800'));
const SHOP = image(unsplash('1509440159596-0249088772ff', 'w=1200&h=900'));
const SHOP_2 = image(unsplash('1555507036-ab1f4038808a', 'w=1200&h=900'));
const VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/23056/23056-720.mp4', kind: 'video' };
const PORTRAIT_VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/40246/40246-720.mp4', kind: 'video' };

const pin = (id: string, card: Partial<SocialPostPreviewCardMetadata>): SocialPostPreviewCardMetadata => ({
  network: 'pinterest',
  networkLabel: 'Pinterest',
  postType: 'pin',
  accountLabel: 'Feature Frenzy 2',
  media: [PIN_TALL],
  mediaCount: 1,
  decisionId: `pin-${id}`,
  taskId: `pin-task-${id}`,
  labels: { ...LABELS, postType: 'Pin' },
  ...card,
});

const local = (id: string, card: Partial<SocialPostPreviewCardMetadata>): SocialPostPreviewCardMetadata => ({
  network: 'google_business',
  networkLabel: 'Google Business Profile',
  postType: 'post',
  media: [],
  mediaCount: 0,
  decisionId: `gbp-${id}`,
  taskId: `gbp-task-${id}`,
  labels: LABELS,
  ...card,
});

export const PINTEREST_GBP_SAMPLES: { id: string; title: string; card: SocialPostPreviewCardMetadata }[] = [
  {
    id: 'pin-full',
    title: 'Pinterest: tall pin with a title, description and link',
    card: pin('full', {
      caption: 'Sunday morning yoga flow\n\nA calm 30-minute flow for every level. Save it for your next slow morning. #yoga #morningroutine',
      link: 'https://www.featurefrenzy2.com/yoga',
    }),
  },
  {
    id: 'pin-square',
    title: 'Pinterest: square pin, description only, scheduled',
    card: pin('square', {
      caption: 'Five kettlebell moves you can do at home.',
      media: [PIN_SQUARE],
      scheduledAt: '2026-10-30T07:30:00.000Z',
    }),
  },
  {
    id: 'pin-carousel',
    title: 'Pinterest: carousel pin, four items',
    card: pin('carousel', {
      caption: 'Studio tour\n\nThe new strength room, the yoga loft and the recovery corner.',
      media: [PIN_TALL, PIN_WIDE, PORTRAIT_VIDEO, PIN_SQUARE],
      mediaCount: 4,
      link: 'https://www.featurefrenzy2.com/tour',
    }),
  },
  {
    id: 'pin-video',
    title: 'Pinterest: video pin',
    card: pin('video', {
      caption: 'Box jumps, round two\n\nScaled for every level.',
      media: [PORTRAIT_VIDEO],
      postType: 'video',
      labels: { ...LABELS, postType: 'Video' },
    }),
  },
  {
    id: 'pin-long',
    title: 'Pinterest: long description, one item not shown',
    card: pin('long', {
      caption:
        'Winter workout plan\n\nShorter days do not have to mean fewer workouts. This four-week plan mixes strength, mobility and a little cardio, and every session fits in 35 minutes. Print it, pin it to the fridge and tick off each day. #fitness #winterworkout #homeworkout',
      media: [PIN_WIDE],
      mediaCount: 2,
      link: 'https://www.featurefrenzy2.com/winter',
      labels: { ...LABELS, postType: 'Pin', mediaNotShown: 'Photos or videos not shown here: 1' },
    }),
  },
  {
    id: 'pin-hebrew',
    title: 'Pinterest: Hebrew title and description',
    card: pin('hebrew', {
      caption: 'יוגה של בוקר\n\nשיעור רגוע של 30 דקות לכל הרמות. #יוגה',
      link: 'https://www.featurefrenzy2.com/yoga',
    }),
  },
  {
    id: 'pin-broken',
    title: 'Pinterest: picture that fails to load',
    card: pin('broken', {
      caption: 'Desk stretches\n\nFive minutes between meetings.',
      media: [image('https://images.unsplash.com/photo-0000000000000-missing?w=900')],
    }),
  },
  {
    id: 'gbp-photo',
    title: 'Google Business Profile: photo, text and a call to action',
    card: local('photo', {
      caption: 'Fresh sourdough every Friday. Come early, it sells out by noon! #sourdough',
      media: [SHOP],
      mediaCount: 1,
      link: 'https://www.featurefrenzy2.com/menu',
    }),
  },
  {
    id: 'gbp-event',
    title: 'Google Business Profile: event title and summary, text only',
    card: local('event', {
      caption: 'Holiday market\nJoin us Saturday from 9am for mulled cider, fresh bakes and live music in the courtyard.',
    }),
  },
  {
    id: 'gbp-link',
    title: 'Google Business Profile: call to action only',
    card: local('link', { link: 'https://www.featurefrenzy2.com/book' }),
  },
  {
    id: 'gbp-photo-only',
    title: 'Google Business Profile: photo only',
    card: local('photo-only', { media: [SHOP_2], mediaCount: 1 }),
  },
  {
    id: 'gbp-two',
    title: 'Google Business Profile: two photos, one not shown, long text',
    card: local('two', {
      caption:
        'We turned ten this week. Ten years ago Joe opened the door at 5am with one oven and a borrowed mixer. Today we bake for three neighbourhoods, two schools and a hospital. Thank you to every regular who came back, and to the team who shows up before the sun does. Here is to the next ten.',
      media: [SHOP, SHOP_2],
      mediaCount: 3,
      link: 'https://www.featurefrenzy2.com/story',
      labels: { ...LABELS, mediaNotShown: 'Photos or videos not shown here: 1' },
    }),
  },
  {
    id: 'gbp-video',
    title: 'Google Business Profile: video',
    card: local('video', { caption: 'Rope day at the studio.', media: [VIDEO], mediaCount: 1 }),
  },
];
